import PropTypes from 'prop-types';
import React, { useState, FC, useRef, useEffect } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Stack,
  Tooltip,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Icons,
  DialogFromMui as Dialog,
  DialogContent,
  DialogTitleFromMui as DialogTitle,
  DialogActions,
  ListFromMui as List,
  ListItemFromMui as ListItem,
  ListItemAvatarFromMui as ListItemAvatar,
  ListItemTextFromMui as ListItemText,
} from '@ublend-npm/aulaui-core';
import push from '../../../../../../utils/routing';
import { analyticsTrackEvent } from '@core/utils/analytics';
import { MESSAGE_PARTICIPANT_REMOVED } from '@core/constants/analytics';

interface Participant {
  objectId: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  online?: boolean;
}

interface ConversationParticipantsListResponsiveProps {
  handleClose: () => void;
  groupId: string;
  boundToSpaceGroup?: boolean;
  openn: boolean;
  participants: Participant[];
  classRoomId: string;
  userId: string;
  educatorOnly?: boolean;
  removeParticipant?: (participantId: string, groupId: string) => Promise<void>;
}

const ConversationParticipantsListResponsive: FC<
  ConversationParticipantsListResponsiveProps
> = ({
  handleClose,
  groupId,
  boundToSpaceGroup,
  participants,
  classRoomId,
  userId,
  educatorOnly,
  openn,
  removeParticipant,
}) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] =
    useState<Participant | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Accessibility refs
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstParticipantRef = useRef<HTMLLIElement>(null);

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (!isLgUp && openn) {
      // Add a small delay to ensure state is properly updated
      const timer = setTimeout(() => {
        const targetPath = `dashboard/${classRoomId}/messages/${groupId}/view-participants`;
        // Check if we're already on the target path to avoid the warning
        if (window.location.hash !== `#${targetPath}`) {
          push(targetPath);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLgUp, openn, classRoomId, groupId]);

  // Focus management for accessibility
  useEffect(() => {
    if (openn) {
      // Focus the back button on small devices, or first focusable element on desktop
      if (!isLgUp && backButtonRef.current) {
        backButtonRef.current.focus();
      } else if (isLgUp && dialogRef.current) {
        // On desktop, focus the dialog container
        dialogRef.current.focus();
      }
    }
  }, [openn, isLgUp]);

  // Keyboard navigation handler
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  const handleRemoveClick = (
    participant: Participant,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedParticipant(participant);
    setRemoveError(null); // Clear any previous errors
    setRemoveDialogOpen(true);
  };

  const handleCloseRemoveDialog = () => {
    setRemoveDialogOpen(false);
    setSelectedParticipant(null);
    setRemoveError(null);
  };

  const handleClosePage = () => {
    handleClose();
  };

  const handleConfirmRemove = () => {
    if (!selectedParticipant || !removeParticipant) return;

    setIsRemoving(true);
    setRemoveError(null); // Clear any previous errors

    removeParticipant(selectedParticipant.objectId, groupId)
      .then(() => {
        setRemoveDialogOpen(false);
        setIsRemoving(false);
        setRemoveError(null);
        // Trigger analytics event for participant removal attempt
        analyticsTrackEvent(MESSAGE_PARTICIPANT_REMOVED);
      })
      .catch((error) => {
        // Extract error message from the error object
        let errorMessage = 'Failed to remove participant. Please try again.';

        if (error?.response?.data?.errors?.[0]?.detail) {
          errorMessage = error.response.data.errors[0].detail;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setRemoveError(errorMessage);
        setIsRemoving(false);
        // Don't close the dialog so user can see the error
      });
  };

  const content = (
    <Box
      sx={{
        width: '100%',
        pl: { xs: 2, md: 0 },
        pr: { xs: 2, md: 0 },
        pb: { xs: 12, sm: 12, md: 0 },
      }}
    >
      <Typography
        variant="subtitle2"
        color={theme.palette.text.secondary}
        sx={{ mb: 1, mt: { xs: 1, sm: 1 } }}
      >
        Members
      </Typography>
      <List
        role="list"
        aria-label="Participants"
        sx={{
          width: '100%',
          padding: 0,
          '& .MuiListItem-root': {
            paddingLeft: { xs: 1, sm: 2 },
            paddingRight: { xs: 1, sm: 2 },
          },
        }}
      >
        {(participants || [])
          .filter((u) => u.objectId !== userId)
          .map((p, index) => {
            return (
              <React.Fragment key={p.objectId}>
                <ListItem
                  role="listitem"
                  tabIndex={0}
                  aria-level={1}
                  ref={index === 0 ? firstParticipantRef : undefined}
                  aria-label={`${p.firstName} ${p.lastName}${
                    p.online ? ', online' : ''
                  }`}
                  sx={{
                    gap: '8px',
                    pr: 0,
                    pl: 0,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    // Disable hover on mobile devices
                    '@media (hover: none)': {
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    },
                  }}
                  secondaryAction={
                    // Show remove icon only if conditions allow it
                    !(
                      boundToSpaceGroup ||
                      educatorOnly ||
                      participants.length <= 2
                    ) &&
                    p.objectId !== userId && (
                      <Tooltip
                        title="Remove"
                        placement="top"
                        arrow
                        enterDelay={300}
                        leaveDelay={0}
                        disableFocusListener={false}
                        disableHoverListener={false}
                        disableTouchListener={false}
                        PopperProps={{
                          sx: { zIndex: 9999 },
                        }}
                      >
                        <span>
                          <IconButton
                            edge="end"
                            color="primary"
                            aria-label={`Remove ${p.firstName} ${p.lastName} from conversation`}
                            onClick={(event) => handleRemoveClick(p, event)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleRemoveClick(p);
                              }
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                            tabIndex={0}
                            aria-level={1}
                          >
                            <Icons.RemoveCircleOutlined color="primary" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemAvatar
                    sx={{
                      minWidth: '36px !important',
                      height: '32px !important',
                    }}
                  >
                    {p.online ? (
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        variant="dot"
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: '#44b700',
                            color: '#44b700',
                            '&::after': {
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              animation: 'ripple 1.2s infinite ease-in-out',
                              border: '1px solid currentColor',
                              content: '""',
                            },
                          },
                          '@keyframes ripple': {
                            '0%': {
                              transform: 'scale(.8)',
                              opacity: 1,
                            },
                            '100%': {
                              transform: 'scale(2.4)',
                              opacity: 0,
                            },
                          },
                        }}
                      >
                        <Avatar
                          src={p.avatar}
                          alt={`${p.firstName} ${p.lastName}`}
                          fallbackText={p.firstName?.charAt(0).toUpperCase()}
                          sx={{ width: '32px', height: '32px' }}
                        />
                      </Badge>
                    ) : (
                      <Avatar
                        src={p.avatar}
                        alt={`${p.firstName} ${p.lastName}`}
                        fallbackText={p.firstName?.charAt(0).toUpperCase()}
                        sx={{ width: '32px', height: '32px' }}
                      />
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${p.firstName} ${p.lastName}`}
                    color={theme.palette.text.primary}
                  />
                </ListItem>
                {index < participants.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
      </List>
    </Box>
  );

  return (
    <>
      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Main participants list */}
      {isLgUp ? (
        <Dialog
          open={openn}
          onClose={handleClose}
          maxWidth={false}
          PaperProps={{
            sx: {
              '--Paper-overlay': 'none', // Completely removes the overlay
              backgroundImage: 'none',
              width: '1152px !important',
              height: '706px !important',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: '24px',
            }}
            tabIndex={0}
            aria-label="Conversation participants"
            role="dialog"
            aria-modal="true"
          >
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              Conversation participants
            </Typography>
            <Tooltip
              title="Close"
              placement="top"
              arrow
              enterDelay={300}
              leaveDelay={0}
              disableFocusListener={false}
              disableHoverListener={false}
              disableTouchListener={false}
            >
              <span>
                <IconButton
                  onClick={handleClose}
                  aria-label="Close participants dialog"
                  tabIndex={0}
                >
                  <Icons.Close />
                </IconButton>
              </span>
            </Tooltip>
          </DialogTitle>
          <DialogContent
            role="region"
            aria-label="Participants list"
            tabIndex={0}
          >
            {content}
          </DialogContent>
        </Dialog>
      ) : (
        <Box
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Conversation participants"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            bgcolor: 'background.paper',
          }}
        >
          {/* Fixed Header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 2,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexShrink: 0, // Prevent header from shrinking
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <IconButton
              ref={backButtonRef}
              onClick={handleClosePage}
              aria-label="Go back to conversation"
              tabIndex={0}
              sx={{ color: 'inherit' }}
            >
              <Icons.ArrowBack sx={{ color: 'inherit' }} />
            </IconButton>
            <Typography
              variant="h6"
              fontWeight="bold"
              tabIndex={0}
              role="heading"
              aria-level={1}
            >
              Conversation participants
            </Typography>
          </Stack>

          {/* Scrollable Content */}
          <Box
            role="region"
            aria-label="Participants list"
            tabIndex={0}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 0, // Important for flex child scrolling
              height: '100%',
              WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
              '-webkit-overflow-scrolling': 'touch', // For older iOS versions
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.divider,
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: theme.palette.text.secondary,
              },
              mb: '24px',
            }}
          >
            {content}
          </Box>
        </Box>
      )}

      {/* Remove participant confirmation dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleCloseRemoveDialog}
        PaperProps={{
          sx: {
            '--Paper-overlay': 'none', // Completely removes the overlay
            backgroundImage: 'none',
            '& .MuiDialog-paper': {
              width: '100%',
              maxWidth: 'xs',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            variant: 'title',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: '24px',
            mr: '16px',
            ml: '16px',
            p: 0,
            height: '24px !important',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Remove participant confirmation"
        >
          Remove participant
          {isLgUp && (
            <Tooltip
              title="Close"
              placement="top"
              arrow
              enterDelay={300}
              leaveDelay={0}
              disableFocusListener={false}
              disableHoverListener={false}
              disableTouchListener={false}
            >
              <span>
                <IconButton
                  onClick={handleCloseRemoveDialog}
                  aria-label="Close remove participant dialog"
                  tabIndex={0}
                >
                  <Icons.Close />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent sx={{ m: '24px 16px', p: 0 }}>
          <Typography
            variant="body2"
            tabIndex={0}
            aria-label={`Are you sure you want to remove  ${
              selectedParticipant
                ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}`
                : 'this participant'
            } from the conversation?`}
          >
            Are you sure you want to remove{' '}
            <strong>
              {selectedParticipant
                ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}`
                : 'this participant'}
            </strong>{' '}
            from the conversation?
          </Typography>
          {removeError && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: theme.palette.error.light,
                borderRadius: 1,
                border: `1px solid ${theme.palette.error.main}`,
              }}
            >
              <Typography
                variant="body2"
                color="error"
                sx={{ fontWeight: 'medium' }}
              >
                {removeError}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          // eslint-disable-next-line
          sx={{
            '--Paper-overlay': 'none', // Completely removes the overlay
            backgroundImage: 'none',
            p: 2,
          }}
        >
          <Button
            onClick={handleCloseRemoveDialog}
            disabled={isRemoving}
            aria-label="Cancel removing participant"
            tabIndex={0}
            sx={{
              color: theme.palette.mode === 'dark' ? '#D0BCFA' : '#452188',
              borderColor: `${theme.palette.primary.main}80`,
              background: 'transparent',
              textTransform: 'none',
              fontWeight: 'bold',
              border: '1px solid',
              '&:disabled': {
                color: '#9e9e9e',
                borderColor: '#9e9e9e',
              },
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            disabled={isRemoving}
            aria-label={`Confirm remove ${
              selectedParticipant
                ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}`
                : 'participant'
            }`}
            tabIndex={0}
            sx={{
              textTransform: 'none',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#9183af' : '#30175f',
              },
            }}
          >
            {isRemoving ? (
              <>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px',
                  }}
                />
                Removing...
              </>
            ) : (
              'Yes, Remove'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ConversationParticipantsListResponsive.propTypes = {
  classRoomId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  participants: PropTypes.array.isRequired,
  removeParticipant: PropTypes.func,
  userId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  openn: PropTypes.bool.isRequired,
  boundToSpaceGroup: PropTypes.bool,
  educatorOnly: PropTypes.bool,
};

export default ConversationParticipantsListResponsive;
